import React from 'react';

import RadioButton, { RadioButtonGroup } from 'carbon-react/lib/components/radio-button';
import Box from 'carbon-react/lib/components/box';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';

import { DateRangeFilterFieldConfig } from '../types';
import * as styles from '../filters-sidebar.scss';

interface DateRangeFilterFieldProps {
  config: DateRangeFilterFieldConfig;
  onToggle: (key: string, isActive: boolean) => void;
  onFromChange: (key: string, rawValue: string) => void;
  onToChange: (key: string, rawValue: string) => void;
}

/**
 * Component for rendering a date range filter field in the filters sidebar, allowing users to toggle the filter on/off and select a date range when active
 * @param config - The configuration for the date range filter field
 * @param onToggle - Function to call when the filter is toggled on/off
 * @param onFromChange - Function to call when the "from" date is changed
 * @param onToChange - Function to call when the "to" date is changed
 * @returns A JSX element representing the date range filter field
 */
export const DateRangeFilterField = ({ config, onToggle, onFromChange, onToChange }: DateRangeFilterFieldProps) => {
  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    onToggle(config.key, event.target.value === 'filter');
  };

  const handleFromChange = (e: DateChangeEvent) => {
    onFromChange(config.key, e.target.value.rawValue || '');
  };

  const handleToChange = (e: DateChangeEvent) => {
    onToChange(config.key, e.target.value.rawValue || '');
  };

  return (
    <Box mb={3}>
      <RadioButtonGroup
        name={`filter-date-range-${config.key}`}
        onChange={handleToggle}
        value={config.isActive ? 'filter' : 'all'}
        legend={config.label}
        size="medium"
      >
        <RadioButton value="all" label={config.showAllLabel} checked={!config.isActive} />
        <RadioButton value="filter" label={config.filterByRangeLabel} checked={config.isActive} />
      </RadioButtonGroup>
      {config.isActive && (
        <div style={{ paddingLeft: 16, borderLeft: '2px solid  #CCD6DB' }}>
          <Box display="flex" flexDirection="row" mt={2}>
            <Box mr={2} flex="1" className={styles.carbonDate}>
              <DateInput
                label={config.fromLabel}
                name={`filter-date-from-${config.key}`}
                id={`filter-date-from-${config.key}`}
                value={config.fromValue || ''}
                onChange={handleFromChange}
                width="100%"
                mb={0}
              />
            </Box>
            <Box flex="1" className={styles.carbonDate}>
              <DateInput
                label={config.toLabel}
                name={`filter-date-to-${config.key}`}
                id={`filter-date-to-${config.key}`}
                value={config.toValue || ''}
                onChange={handleToChange}
                width="100%"
                mb={0}
              />
            </Box>
          </Box>
        </div>
      )}
    </Box>
  );
};
